import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { api, GcalStatus, IntegrationStatus, isTauriEnvironment } from "../lib/tauri";
import { useSyncStore } from "../stores/syncStore";
import { useUiStore } from "../stores/uiStore";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Tabs } from "./ui/Tabs";

type Tab = "notion" | "connect" | "display" | "sync" | "shortcuts";

export function SettingsPanel() {
  const { settings, load, saveSettings } = useSyncStore();
  const currentTheme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const [tab, setTab] = useState<Tab>("notion");
  const [token, setToken] = useState("");
  const [pageUrl, setPageUrl] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (settings?.notionParentPageId) setPageUrl(settings.notionParentPageId);
  }, [settings]);

  const updateTheme = (theme: "system" | "light" | "dark") => {
    setTheme(theme);
    setMessage("");
    void saveSettings({ theme }).catch((error) => {
      setMessage(`テーマ保存に失敗しました: ${String(error)}`);
      if (settings?.theme) setTheme(settings.theme);
    });
  };

  const setup = async () => {
    setMessage("確認中...");
    try {
      await api.setupNotionDatabase(token, pageUrl);
      setMessage("DB確認と保存が完了しました");
      await load();
    } catch (e) {
      setMessage(String(e));
    }
  };

  return (
    <div>
      <div className="border-b border-border px-5 py-4 dark:border-border-dark">
        <h2 className="text-lg font-semibold">設定</h2>
      </div>
      <div className="p-5">
        <Tabs value={tab} onChange={setTab} tabs={[{ value: "notion", label: "Notion" }, { value: "connect", label: "接続" }, { value: "display", label: "表示" }, { value: "sync", label: "同期" }, { value: "shortcuts", label: "ショートカット" }]} />
        {tab === "notion" && (
          <div className="mt-5 space-y-4">
            <Input type="password" placeholder={settings?.hasNotionToken ? "保存済み token あり" : "Notion integration token"} value={token} onChange={(e) => setToken(e.target.value)} />
            <Input placeholder="Notion page URL or database_id" value={pageUrl} onChange={(e) => setPageUrl(e.target.value)} />
            <Button variant="primary" onClick={() => void setup()}>DB Setup + Save</Button>
            <div className="rounded-md bg-hover p-3 text-xs text-muted dark:bg-hover-dark dark:text-muted-dark">
              <div>Database: {settings?.notionDatabaseId ?? "-"}</div>
              <div>Data source: {settings?.notionDataSourceId ?? "-"}</div>
              {message && <div className="mt-2 text-text dark:text-text-dark">{message}</div>}
            </div>
          </div>
        )}
        {tab === "connect" && <ConnectTab />}
        {tab === "display" && (
          <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {([
              ["system", "自動"],
              ["light", "ライト"],
              ["dark", "ダーク"],
            ] as const).map(([theme, label]) => (
              <Button
                key={theme}
                type="button"
                aria-pressed={currentTheme === theme}
                variant={currentTheme === theme ? "primary" : "outline"}
                className="min-h-11"
                onClick={() => updateTheme(theme)}
              >
                {label}
              </Button>
            ))}
            {message && <p className="sm:col-span-3 text-xs text-muted dark:text-muted-dark">{message}</p>}
          </div>
        )}
        {tab === "sync" && (
          <div className="mt-5 space-y-3">
            <Input type="number" min={60} defaultValue={settings?.syncIntervalSec ?? 300} onBlur={(e) => void saveSettings({ syncIntervalSec: Number(e.target.value) })} />
            <p className="text-xs text-muted dark:text-muted-dark">競合はローカル未送信変更を優先します。</p>
          </div>
        )}
        {tab === "shortcuts" && <ShortcutList />}
      </div>
    </div>
  );
}

function ConnectTab() {
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [issuedToken, setIssuedToken] = useState("");
  const [message, setMessage] = useState("");

  const refresh = async () => {
    try {
      const s = await api.getIntegrationStatus!();
      setStatus(s);
    } catch (e) {
      setMessage(String(e));
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isTauriEnvironment()) {
    return (
      <p className="mt-5 text-sm text-muted dark:text-muted-dark">
        接続トークンはデスクトップ版でのみ管理できます。
      </p>
    );
  }

  const issue = async () => {
    setMessage("");
    try {
      const token = await api.issueIntegrationToken!();
      setIssuedToken(token);
      await refresh();
    } catch (e) {
      setMessage(String(e));
    }
  };

  const revoke = async () => {
    setMessage("");
    try {
      await api.revokeIntegrationToken!();
      setIssuedToken("");
      await refresh();
      setMessage("トークンを失効しました");
    } catch (e) {
      setMessage(String(e));
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(issuedToken);
    setMessage("コピーしました");
  };

  return (
    <div className="mt-5 space-y-4">
      <GcalConnectSection />
      <div className="rounded-md bg-hover p-3 text-xs text-muted dark:bg-hover-dark dark:text-muted-dark">
        <div>状態: {status?.hasToken ? "トークン発行済み" : "未発行"}</div>
        <div>
          ローカルAPI:{" "}
          {status?.apiPort
            ? `http://127.0.0.1:${status.apiPort}`
            : "未記録（アプリ起動時に自動記録されます）"}
        </div>
      </div>
      <Button variant="primary" onClick={() => void issue()}>
        {status?.hasToken ? "トークンを再発行（旧トークンは失効）" : "トークンを発行"}
      </Button>
      {issuedToken && (
        <div className="space-y-2 rounded-md border border-border p-3 dark:border-border-dark">
          <p className="text-xs text-muted dark:text-muted-dark">
            このトークンは今だけ表示されます。コピーして MCP 登録（tools/flow-mcp/README.md）に使ってください。
          </p>
          <code className="block break-all text-xs">{issuedToken}</code>
          <Button variant="outline" onClick={() => void copy()}>コピー</Button>
        </div>
      )}
      {status?.hasToken && (
        <Button variant="outline" onClick={() => void revoke()}>トークンを失効</Button>
      )}
      {message && <p className="text-xs">{message}</p>}
    </div>
  );
}

function GcalConnectSection() {
  const [status, setStatus] = useState<GcalStatus | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const s = await api.gcalStatus!();
      setStatus(s);
    } catch (e) {
      setMessage(String(e));
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    setMessage("ブラウザでGoogle同意画面を開いています");
    try {
      const s = await api.gcalConnect!();
      setStatus(s);
      setMessage("Googleカレンダーに接続しました");
    } catch (e) {
      setMessage(String(e));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setMessage("");
    try {
      const s = await api.gcalDisconnect!();
      setStatus(s);
      setMessage("Googleカレンダー連携を解除しました");
    } catch (e) {
      setMessage(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-md border border-border p-3 dark:border-border-dark">
      <div className="mb-3 flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-brand-600" />
        <h3 className="text-sm font-semibold">Googleカレンダー連携</h3>
      </div>
      <div className="mb-3 rounded-md bg-hover p-3 text-xs text-muted dark:bg-hover-dark dark:text-muted-dark">
        <div>状態: {status?.connected ? "接続済み" : "未接続"}</div>
        <div>アカウント: {status?.email ?? (status?.connected ? "接続済み" : "-")}</div>
        <div>OAuth設定: {status?.hasClientConfig ? "設定済み" : "未設定"}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          disabled={busy || !status?.hasClientConfig}
          onClick={() => void connect()}
        >
          {status?.connected ? "再接続" : "Googleに接続"}
        </Button>
        {status?.connected && (
          <Button variant="outline" disabled={busy} onClick={() => void disconnect()}>
            解除
          </Button>
        )}
      </div>
      {!status?.hasClientConfig && (
        <p className="mt-2 text-xs text-muted dark:text-muted-dark">
          `~/.flow/config.json` に gcalClientId / gcalClientSecret を追加してください。
        </p>
      )}
      {message && <p className="mt-2 break-words text-xs">{message}</p>}
    </section>
  );
}

function ShortcutList() {
  return (
    <div className="mt-5 grid grid-cols-2 gap-2 text-sm">
      {["⌘N 新規", "⌘F 検索", "⌘1-5 フィルタ", "⌘R 同期", "⌘, 設定", "? 一覧", "Esc 閉じる", "⌘Z Undo"].map((x) => <div key={x} className="rounded border border-border p-2 dark:border-border-dark">{x}</div>)}
    </div>
  );
}
